import { makeAutoObservable, reaction } from 'mobx'
import { appearanceService, type AppearanceSettings, type AppliedAppearance, type FontKey } from '@/services'
import { COLOR_SCHEMES, schemeVars, type SchemeKey } from './colorSchemes'

export const DEFAULT_APPEARANCE: AppearanceSettings = {
  scheme: 'ocean',
  accent: '#0158C5',
  font: 'geist',
  textSize: 'md',
  radius: 'default',
  spacing: 'default',
  controlSize: 'md',
  background: 'ambient',
  glass: true,
  motion: 'full',
  sidebarSide: 'top',
  navFill: '',
  contentWidth: 'full',
  zebra: false,
  numbers: 'tabular',
  editorMode: 'auto',
  drawerSide: 'right',
  drawerWidth: 'standard',
  backdrop: 'blur',
  outsideClose: true,
  toastPosition: 'bottom-right',
}

/** Top menu fills: a dark first row whose light tint fills the second (blue → light blue). */
export const NAV_FILL_PRESETS = ['#0158C5', '#1E3A8A', '#0F766E', '#B91C1C', '#16191D'] as const

export const ACCENT_PRESETS = ['#0158C5', '#DC0C17', '#16191D', '#2563EB', '#4F46E5', '#047857', '#B45309', '#3F4A55'] as const

/** Every family here ships Cyrillic, so Russian and Uzbek (Cyrillic) render in the chosen face. */
export const FONT_STACKS: Record<FontKey, { name: string; family: string; google?: string }> = {
  geist: { name: 'Geist', family: "'Geist', ui-sans-serif, system-ui, sans-serif" },
  inter: { name: 'Inter', family: "'Inter', ui-sans-serif, system-ui, sans-serif", google: 'Inter:wght@400;500;600;700' },
  golos: { name: 'Golos Text', family: "'Golos Text', ui-sans-serif, system-ui, sans-serif", google: 'Golos+Text:wght@400;500;600;700' },
  onest: { name: 'Onest', family: "'Onest', ui-sans-serif, system-ui, sans-serif", google: 'Onest:wght@400;500;600;700' },
  manrope: { name: 'Manrope', family: "'Manrope', ui-sans-serif, system-ui, sans-serif", google: 'Manrope:wght@400;500;600;700' },
}

export function fontHref(font: FontKey) {
  const google = FONT_STACKS[font].google
  return google ? `https://fonts.googleapis.com/css2?family=${google}&display=swap` : null
}

export const TEXT_SCALES: Record<AppearanceSettings['textSize'], number> = { sm: 0.93, md: 1, lg: 1.07, xl: 1.14 }
export const RADIUS_SCALES: Record<AppearanceSettings['radius'], number> = { sharp: 0.35, default: 1, soft: 1.45 }
export const SPACE_SCALES: Record<AppearanceSettings['spacing'], number> = { compact: 0.85, default: 1, relaxed: 1.18 }
export const CONTROL_SCALES: Record<AppearanceSettings['controlSize'], number> = { xs: 0.82, sm: 0.91, md: 1, lg: 1.12 }

function channels(hex: string) {
  const n = parseInt(hex.slice(1), 16)
  return [n >> 16, (n >> 8) & 255, n & 255]
}
function toHex(rgb: number[]) {
  return `#${rgb.map((c) => Math.round(Math.min(255, Math.max(0, c))).toString(16).padStart(2, '0')).join('')}`.toUpperCase()
}
function luminance(hex: string) {
  const [r, g, b] = channels(hex).map((c) => {
    const v = c / 255
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}
/** A lighter tint of the accent that stays readable on the dark canvas. */
function darkVariant(hex: string) {
  const [r, g, b] = channels(hex)
  const mix = luminance(hex) < 0.2 ? 0.48 : 0.25
  return toHex([r + (255 - r) * mix, g + (255 - g) * mix, b + (255 - b) * mix])
}
const readableOn = (hex: string) => (luminance(hex) > 0.4 ? '#0B0E10' : '#FFFFFF')
const validHex = (value: string) => /^#[0-9a-f]{6}$/i.test(value)

export function computeApplied(settings: AppearanceSettings): AppliedAppearance {
  const schemeKey = settings.scheme in COLOR_SCHEMES ? settings.scheme : DEFAULT_APPEARANCE.scheme
  const scheme = COLOR_SCHEMES[schemeKey]
  const accent = validHex(settings.accent) ? settings.accent.toUpperCase() : DEFAULT_APPEARANCE.accent
  // A scheme's own accent may bring a hand-picked dark tint; any other accent is lightened.
  const accentDark = accent === scheme.accent && scheme.accentDark ? scheme.accentDark : darkVariant(accent)
  const navFill = validHex(settings.navFill) ? settings.navFill.toUpperCase() : ''
  return {
    vars: {
      '--user-accent': accent,
      '--user-accent-fg': readableOn(accent),
      '--user-accent-dark': accentDark,
      '--user-accent-dark-fg': readableOn(accentDark),
      '--app-font': FONT_STACKS[settings.font].family,
      '--text-scale': String(TEXT_SCALES[settings.textSize]),
      '--radius-scale': String(RADIUS_SCALES[settings.radius]),
      '--space-scale': String(SPACE_SCALES[settings.spacing]),
      '--ui-size': String(CONTROL_SCALES[settings.controlSize] ?? 1),
      ...schemeVars(schemeKey),
      ...(navFill && { '--nav-fill': navFill, '--nav-fill-fg': readableOn(navFill) }),
    },
    attrs: {
      bg: settings.background,
      glass: settings.glass ? 'on' : 'off',
      motion: settings.motion,
      backdrop: settings.backdrop,
      zebra: settings.zebra ? 'on' : 'off',
      numbers: settings.numbers,
      scheme: settings.scheme,
      sidebar: settings.sidebarSide,
      navFill: navFill ? 'on' : 'off',
    },
  }
}

export function loadFont(font: FontKey) {
  const href = fontHref(font)
  if (!href || document.querySelector(`link[href="${href}"]`)) return
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  document.head.appendChild(link)
}

/**
 * Interface appearance for the whole console. Every change applies to <html>
 * immediately (CSS variables + data attributes) and is persisted.
 * Theme mode and table density stay in UiStore, which already owns them.
 */
export class AppearanceStore {
  settings: AppearanceSettings = AppearanceStore.restore()

  private static restore(): AppearanceSettings {
    return AppearanceStore.merge(appearanceService.readCached())
  }

  /** Stored values merged onto defaults; unknown or retired values fall back. */
  private static merge(cached: Partial<AppearanceSettings> | null): AppearanceSettings {
    const merged = { ...DEFAULT_APPEARANCE, ...cached }
    // First run: follow the device's reduce-motion preference until the user picks in Appearance.
    if (!cached?.motion && window.matchMedia('(prefers-reduced-motion: reduce)').matches) merged.motion = 'reduced'
    if (!(merged.font in FONT_STACKS)) merged.font = DEFAULT_APPEARANCE.font
    // A retired scheme (teal, forest, plum, graphite, sand) falls back with its accent.
    if (!(merged.scheme in COLOR_SCHEMES)) {
      merged.scheme = DEFAULT_APPEARANCE.scheme
      merged.accent = DEFAULT_APPEARANCE.accent
    }
    if (!(merged.controlSize in CONTROL_SCALES)) merged.controlSize = DEFAULT_APPEARANCE.controlSize
    return merged
  }

  constructor() {
    makeAutoObservable(this)
    this.apply()
    reaction(
      () => ({ ...this.settings }),
      (settings) => {
        this.apply()
        void appearanceService.save(settings, computeApplied(settings))
      },
    )
  }

  get isDefault() {
    return JSON.stringify(this.settings) === JSON.stringify(DEFAULT_APPEARANCE)
  }

  set<K extends keyof AppearanceSettings>(key: K, value: AppearanceSettings[K]) {
    this.settings = { ...this.settings, [key]: value }
  }

  /** A scheme brings its own accent; a custom accent can still be picked afterwards. */
  applyScheme(scheme: SchemeKey) {
    this.settings = { ...this.settings, scheme, accent: COLOR_SCHEMES[scheme].accent }
  }

  reset() {
    this.settings = { ...DEFAULT_APPEARANCE }
  }

  /** Takes a whole saved look (the account's settings document) over the defaults. */
  replace(saved: Partial<AppearanceSettings> | null) {
    this.settings = AppearanceStore.merge(saved)
  }

  private apply() {
    const root = document.documentElement
    const { vars, attrs } = computeApplied(this.settings)
    for (const [name, value] of Object.entries(vars)) root.style.setProperty(name, value)
    for (const [name, value] of Object.entries(attrs)) root.dataset[name] = value
    loadFont(this.settings.font)
  }
}
